import { Component, Host, h, Prop, Fragment, Element, Watch, State, Listen } from '@stencil/core';
import { DomService, deepParse } from '../../services';
import { ButtonAppearance } from '../rcm-button/rcm-button.types';
import { Teaser } from '../rcm-editorial-item-two-blocks/rcm-editorial-item-two-blocks';
import { RcmDownloadProp } from '../rcm-generic-link/rcm-generic-link.types';
import { Ratio, RcmImageProp } from '../rcm-image/rcm-image.types';
import { LinkObject } from '../types/rcm-articles.types';
import { Target } from '../types/link.types';

export type Article = {
  page: {
    imageSrc: RcmImageProp | string;
    headline: string;
    text: string;
    link: LinkObject;
  };
  linkText: string;
};
@Component({
  tag: 'rcm-editorial-item-three-blocks',
  styleUrl: 'rcm-editorial-item-three-blocks.scss',
  shadow: true,
})
export class RcmEditorialItemThreeBlocks {
  @Element() el: HTMLElement;

  @Prop() imageSrcOne: RcmImageProp | string;

  @Prop() headlineOne: string;

  @Prop() textOne: string;

  @Prop() linkOne: string;

  @Prop() linkTextOne: string;

  @Prop() downloadOne: string | RcmDownloadProp;

  @Prop() downloadTextOne: string;

  @Prop() imageSrcTwo: RcmImageProp | string;

  @Prop() headlineTwo: string;

  @Prop() textTwo: string;

  @Prop() linkTwo: string;

  @Prop() linkTextTwo: string;

  @Prop() downloadTwo: string | RcmDownloadProp;

  @Prop() downloadTextTwo: string;

  @Prop() imageSrcThree: RcmImageProp | string;

  @Prop() headlineThree: string;

  @Prop() textThree: string;

  @Prop() linkThree: string;

  @Prop() linkTextThree: string;

  @Prop() downloadThree: string | RcmDownloadProp;

  @Prop() downloadTextThree: string;

  @Prop() squareImages = false;

  @Prop() verticalOffset = false;

  @Prop({ mutable: true }) teasers: string | Teaser[];

  @State() isMediumOrAbove = false;

  componentWillLoad() {
    if (typeof this.teasers === 'string') {
      this.teasers = deepParse(this.teasers);
    }
    if (typeof this.downloadOne === 'string') {
      this.downloadOne = deepParse(this.downloadOne);
    }
    if (typeof this.downloadTwo === 'string') {
      this.downloadTwo = deepParse(this.downloadTwo);
    }
    if (typeof this.downloadThree === 'string') {
      this.downloadThree = deepParse(this.downloadThree);
    }
    if (typeof this.linkOne === 'string') {
      this.linkOne = deepParse(this.linkOne);
    }
    if (typeof this.linkTwo === 'string') {
      this.linkTwo = deepParse(this.linkTwo);
    }
    if (typeof this.linkThree === 'string') {
      this.linkThree = deepParse(this.linkThree);
    }
  }

  isDisplayedTeaser(teaser: Teaser) {
    if (DomService.isSmallScreen()) {
      return (
        [teaser.linkText, teaser.text, teaser.headline, teaser.downloadText, teaser.imageSrc.src].some((value) => !!value) || this.isMediumOrAbove
      );
    }
    return true;
  }

  @Watch('teasers')
  updateTeasers() {
    if (typeof this.teasers === 'string') {
      this.teasers = deepParse(this.teasers);
    }
  }

  @Listen('resize', { target: 'window' })
  handleResize() {
    DomService.fireWhenBelow('medium', () => {
      this.isMediumOrAbove = false;
    });
    DomService.fireWhenAboveOrEqual('medium', () => {
      this.isMediumOrAbove = true;
    });
  }

  render() {
    const items =
      this.teasers?.length > 0
        ? (this.teasers as Teaser[])
        : [
            {
              imageSrc: this.imageSrcOne,
              headline: this.headlineOne,
              text: this.textOne,
              linkText: this.linkTextOne,
              link: this.linkOne,
              downloadText: this.downloadTextOne,
              download: this.downloadOne,
            },
            {
              imageSrc: this.imageSrcTwo,
              headline: this.headlineTwo,
              text: this.textTwo,
              linkText: this.linkTextTwo,
              link: this.linkTwo,
              downloadText: this.downloadTextTwo,
              download: this.downloadTwo,
            },
            {
              imageSrc: this.imageSrcThree,
              headline: this.headlineThree,
              text: this.textThree,
              linkText: this.linkTextThree,
              link: this.linkThree,
              downloadText: this.downloadTextThree,
              download: this.downloadThree,
            },
          ];
    return (
      <Host>
        <rcm-container>
          <rcm-columns class={{ 'blocks--with-offset': this.verticalOffset }}>
            {items.map((article, i) => {
              return (
                this.isDisplayedTeaser(article) && (
                  <rcm-column span={4} key={i}>
                    <rcm-image
                      fullHeight={!this.squareImages}
                      ratio={this.squareImages ? Ratio.SQUARE : Ratio.HD}
                      src={article.imageSrc}
                      overrideMaxWidth={800}
                    />
                    <rcm-headline class="test" level={3} headline={article.headline} color="var(--colors-dark-base)" />
                    {!article.linkText &&
                      !article.downloadText &&
                      (article.link.src !== '/' ? (
                        <rcm-wrapper-link link={article.link} trackingLinkText={article.text} parentComponent="rcm-editorial-item-three-blocks">
                          <p class="content">{article.text}</p>
                        </rcm-wrapper-link>
                      ) : (
                        <p class="content">{article.text}</p>
                      ))}
                    {(article.linkText || article.downloadText) && (
                      <Fragment>
                        <p class="content">{article.text}</p>
                        <div class="link-container">
                          {article.downloadText && (
                            <rcm-button
                              text={article.downloadText}
                              link={{ src: article.download.src, target: Target.BLANK }}
                              icon="download"
                              appearance={ButtonAppearance.UNDERLINE_ICON}
                              download={true}
                            />
                          )}
                          {article.linkText && (
                            <rcm-button
                              text={article.linkText}
                              link={article.link}
                              appearance={ButtonAppearance.TERTIARY}
                              parentComponent="rcm-editorial-item-three-blocks"
                            />
                          )}
                        </div>
                      </Fragment>
                    )}
                  </rcm-column>
                )
              );
            })}
          </rcm-columns>
        </rcm-container>
      </Host>
    );
  }
}
